import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useGroupsStore from '../../stores/groupsStore';
import useAttendanceStore from '../../stores/attendanceStore';
import useStudentsStore from '../../stores/studentsStore';
import useScheduleStore from '../../stores/scheduleStore';
import Sidebar from '../../components/Admin/Sidebar';
import AdminBottomNavBar from '../../components/Admin/AdminBottomNavBar';
import { formatDateToRussian } from '../../utils/dateUtils';
import './AdminGroupAttendance.css';

const AdminGroupAttendance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getGroup } = useGroupsStore();
  const { fetchGroupAttendance, markAttendance } = useAttendanceStore();
  const { fetchGroupSchedule } = useScheduleStore();
  const { students } = useStudentsStore();
  
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [attendanceData, setAttendanceData] = useState({});
  const [scheduleData, setScheduleData] = useState([]);
  const [saving, setSaving] = useState({});

  // Загружаем группу
  useEffect(() => {
    const loadGroup = async () => {
      try {
        setLoading(true);
        const groupData = await getGroup(id);
        setGroup(groupData);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    loadGroup();
  }, [id, getGroup]);

  // Получаем диапазон дат для выбранного месяца
  const getMonthRange = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  // Загружаем расписание группы для выбранного месяца
  useEffect(() => {
    if (!group || !selectedMonth) return;
    
    const loadSchedule = async () => {
      try {
        const { start, end } = getMonthRange();
        
        // Вычитаем 1 день для компенсации смещения на бэкенде
        const adjustedStart = new Date(start);
        adjustedStart.setDate(adjustedStart.getDate() - 1);
        const adjustedEnd = new Date(end);
        adjustedEnd.setDate(adjustedEnd.getDate() - 1);
        
        const schedule = await fetchGroupSchedule({ 
          groupId: id, 
          startDate: adjustedStart.toISOString().split('T')[0], 
          endDate: adjustedEnd.toISOString().split('T')[0] 
        });
        setScheduleData(schedule);
      } catch (e) {
        console.error('Ошибка загрузки расписания:', e);
      }
    };
    
    loadSchedule();
  }, [group, selectedMonth, id, fetchGroupSchedule]);

  // Загружаем посещаемость для выбранного месяца
  useEffect(() => {
    if (!group || !selectedMonth) return;
    
    const loadAttendance = async () => {
      try {
        const { start, end } = getMonthRange();
        
        // Вычитаем 1 день для компенсации смещения на бэкенде
        const adjustedStart = new Date(start);
        adjustedStart.setDate(adjustedStart.getDate() - 1);
        const adjustedEnd = new Date(end);
        adjustedEnd.setDate(adjustedEnd.getDate() - 1);
        
        const data = await fetchGroupAttendance({ 
          groupId: id, 
          startDate: adjustedStart.toISOString().split('T')[0], 
          endDate: adjustedEnd.toISOString().split('T')[0] 
        });
        
        console.log('Initial attendance data:', data);
        
        // Группируем данные по дате, времени и студентам
        const grouped = {};
        data.forEach(item => {
          // Добавляем +1 день для корректного отображения
          const originalDate = new Date(item.date);
          originalDate.setDate(originalDate.getDate() + 1);
          const displayDate = originalDate.toISOString().split('T')[0];
          
          const dateKey = displayDate;
          const timeKey = `${item.startTime}-${item.endTime}`;
          const fullKey = `${dateKey}_${timeKey}`;
          
          if (!grouped[fullKey]) {
            grouped[fullKey] = {
              date: displayDate,
              startTime: item.startTime,
              endTime: item.endTime,
              subject: item.subject,
              students: {}
            };
          }
          grouped[fullKey].students[item.student._id] = {
            status: item.status,
            studentId: item.student._id,
            studentName: item.student.fullName
          };
        });
        
        console.log('Initial grouped attendance data:', grouped);
        setAttendanceData(grouped);
      } catch (e) {
        console.error('Ошибка загрузки посещаемости:', e);
      }
    };
    
    loadAttendance();
  }, [group, selectedMonth, id, fetchGroupAttendance]);

  // Получаем студентов группы
  const groupStudents = group?.students || [];

  // Получаем все занятия за месяц из расписания
  const getAllLessons = () => {
    const lessons = [];
    scheduleData.forEach(scheduleDoc => {
      if (scheduleDoc.timeSlots && Array.isArray(scheduleDoc.timeSlots)) {
        scheduleDoc.timeSlots.forEach(slot => {
          if (slot.startTime && slot.endTime) {
            // Добавляем +1 день для корректного отображения
            const originalDate = new Date(scheduleDoc.date);
            originalDate.setDate(originalDate.getDate() + 1);
            const displayDate = originalDate.toISOString().split('T')[0];
            
            lessons.push({
              date: displayDate,
              startTime: slot.startTime,
              endTime: slot.endTime,
              subject: slot.subject,
              scheduleId: scheduleDoc._id,
              slotIndex: slot._id || slot.index
            });
          }
        });
      }
    });
    
    // Сортируем по дате и времени
    return lessons.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }
      return a.startTime.localeCompare(b.startTime);
    });
  };

  const allLessons = getAllLessons();

  const handleAttendanceChange = async (lessonKey, studentId, status) => {
    console.log('handleAttendanceChange called:', { lessonKey, studentId, status });
    const savingKey = `${lessonKey}-${studentId}`;
    setSaving(prev => ({ ...prev, [savingKey]: true }));
    
    try {
      const [date, timeKey] = lessonKey.split('_');
      const [startTime, endTime] = timeKey.split('-');
      
      console.log('Parsed data:', { date, timeKey, startTime, endTime });
      
      // Вычитаем 1 день для компенсации смещения на бэкенде
      const adjustedDate = new Date(date);
      adjustedDate.setDate(adjustedDate.getDate() - 1);
      const backendDate = adjustedDate.toISOString().split('T')[0];
      
      console.log('Sending to backend:', {
        groupId: id,
        studentId: studentId,
        date: backendDate,
        startTime,
        endTime,
        status
      });
      
      // Находим предмет из расписания
      const lesson = allLessons.find(l => 
        l.date === date && 
        l.startTime === startTime && 
        l.endTime === endTime
      );
      
      const result = await markAttendance({
        groupId: id,
        studentId: studentId,
        date: backendDate,
        startTime,
        endTime,
        subjectId: lesson?.subject?._id || lesson?.subject,
        status
      });
      
      console.log('markAttendance result:', result);
      
      // Перезагружаем данные посещаемости
      const { start, end } = getMonthRange();
      const adjustedStart = new Date(start);
      adjustedStart.setDate(adjustedStart.getDate() - 1);
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() - 1);
      
      const data = await fetchGroupAttendance({ 
        groupId: id, 
        startDate: adjustedStart.toISOString().split('T')[0], 
        endDate: adjustedEnd.toISOString().split('T')[0] 
      });
      
      console.log('Fetched attendance data after save:', data);
      
      // Группируем данные по дате, времени и студентам
      const grouped = {};
      data.forEach(item => {
        // Добавляем +1 день для корректного отображения
        const originalDate = new Date(item.date);
        originalDate.setDate(originalDate.getDate() + 1);
        const displayDate = originalDate.toISOString().split('T')[0];
        
        const dateKey = displayDate;
        const timeKey = `${item.startTime}-${item.endTime}`;
        const fullKey = `${dateKey}_${timeKey}`;
        
        if (!grouped[fullKey]) {
          grouped[fullKey] = {
            date: displayDate,
            startTime: item.startTime,
            endTime: item.endTime,
            subject: item.subject,
            students: {}
          };
        }
        grouped[fullKey].students[item.student._id] = {
          status: item.status,
          studentId: item.student._id,
          studentName: item.student.fullName
        };
      });
      
      console.log('Grouped attendance data after save:', grouped);
      setAttendanceData(grouped);
    } catch (e) {
      console.error('Ошибка сохранения посещаемости:', e);
      alert(`Ошибка сохранения: ${e.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  const handleBulkAttendance = async (lessonKey, status) => {
    try {
      const students = groupStudents.map(s => s._id);
      const [date, timeKey] = lessonKey.split('_');
      const [startTime, endTime] = timeKey.split('-');
      
      // Вычитаем 1 день для компенсации смещения на бэкенде
      const adjustedDate = new Date(date);
      adjustedDate.setDate(adjustedDate.getDate() - 1);
      const backendDate = adjustedDate.toISOString().split('T')[0];
      
      // Находим предмет из расписания
      const lesson = allLessons.find(l => 
        l.date === date && 
        l.startTime === startTime && 
        l.endTime === endTime
      );
      
      // Создаем записи посещаемости для всех студентов
      const promises = students.map(studentId => 
        markAttendance({
          groupId: id,
          studentId: studentId,
          date: backendDate,
          startTime,
          endTime,
          subjectId: lesson?.subject?._id || lesson?.subject,
          status
        })
      );
      
      await Promise.all(promises);
      
      // Перезагружаем данные посещаемости
      const { start, end } = getMonthRange();
      const adjustedStart = new Date(start);
      adjustedStart.setDate(adjustedStart.getDate() - 1);
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() - 1);
      
      const data = await fetchGroupAttendance({ 
        groupId: id, 
        startDate: adjustedStart.toISOString().split('T')[0], 
        endDate: adjustedEnd.toISOString().split('T')[0] 
      });
      
      // Группируем данные по дате, времени и студентам
      const grouped = {};
      data.forEach(item => {
        const dateKey = item.date;
        const timeKey = `${item.startTime}-${item.endTime}`;
        const fullKey = `${dateKey}_${timeKey}`;
        
        if (!grouped[fullKey]) {
          grouped[fullKey] = {
            date: item.date,
            startTime: item.startTime,
            endTime: item.endTime,
            subject: item.subject,
            students: {}
          };
        }
        grouped[fullKey].students[item.student._id] = {
          status: item.status,
          studentId: item.student._id,
          studentName: item.student.fullName
        };
      });
      
      setAttendanceData(grouped);
    } catch (e) {
      console.error('Ошибка массового изменения посещаемости:', e);
      alert(`Ошибка массового изменения: ${e.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return '#27ae60';
      case 'absent': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'present': return '✓';
      case 'absent': return 'H';
      default: return '-';
    }
  };

  // Используем утилитарную функцию для форматирования дат
  const formatDate = formatDateToRussian;

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <div>Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: 'red' }}>
        <div>Ошибка: {error}</div>
      </div>
    );
  }

  if (!group) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <div>Группа не найдена</div>
      </div>
    );
  }

  return (
    <div className='AdminGroupAttendance'>
      <div className="AdminGroupAttendance__container">
        <Sidebar className="admin-sidebar" />
        <div className="admin-content AdminGroupAttendance__content">
          <div className="AdminGroupAttendance__card">
            <div className="AdminGroupAttendance__header">
              <h1 className="AdminGroupAttendance__title">
                Журнал посещаемости группы {group.name}
              </h1>
              <button 
                onClick={() => navigate(`/admin/groups/${id}`)}
                className="AdminGroupAttendance__back-btn"
              >
                Назад к группе
              </button>
            </div>

            <div className="AdminGroupAttendance__month-section">
              <label className="AdminGroupAttendance__month-label">Месяц:</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="AdminGroupAttendance__month-input"
              />
            </div>

            {allLessons.length === 0 ? (
              <div className="AdminGroupAttendance__empty">
                <div className="AdminGroupAttendance__empty-title">На выбранный месяц нет занятий</div>
                <div className="AdminGroupAttendance__empty-text">Выберите другой месяц или добавьте занятия в расписание группы</div>
              </div>
            ) : (
              <div className="AdminGroupAttendance__table-container">
                <table className="AdminGroupAttendance__table">
                  <thead>
                    <tr>
                      <th className="AdminGroupAttendance__th-students">
                        Список студентов
                      </th>
                      {allLessons.map(lesson => {
                        const lessonKey = `${lesson.date}_${lesson.startTime}-${lesson.endTime}`;
                        return (
                          <th key={lessonKey} className="AdminGroupAttendance__th-slot">
                            <div className="AdminGroupAttendance__slot-date">
                              {formatDate(lesson.date)}
                            </div>
                            <div className="AdminGroupAttendance__slot-time">
                              {lesson.startTime} - {lesson.endTime}
                            </div>
                            <div className="AdminGroupAttendance__slot-subject">
                              {lesson.subject?.name || 'Предмет не указан'}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {groupStudents.map(student => (
                      <tr key={student._id}>
                        <td className="AdminGroupAttendance__td-student">
                          {student.fullName}
                        </td>
                        {allLessons.map(lesson => {
                          const lessonKey = `${lesson.date}_${lesson.startTime}-${lesson.endTime}`;
                          const studentAttendance = attendanceData[lessonKey]?.students[student._id];
                          const status = studentAttendance?.status || 'unknown';
                          const savingKey = `${lessonKey}-${student._id}`;
                          
                          return (
                            <td key={lessonKey} className="AdminGroupAttendance__td-attendance">
                              {saving[savingKey] ? (
                                <div className="AdminGroupAttendance__loading">...</div>
                              ) : (
                                <button
                                  onClick={() => {
                                    console.log('Button clicked:', { lessonKey, studentId: student._id, currentStatus: status });
                                    const newStatus = status === 'present' ? 'absent' : 
                                                     status === 'absent' ? 'unknown' : 'present';
                                    console.log('New status will be:', newStatus);
                                    handleAttendanceChange(lessonKey, student._id, newStatus);
                                  }}
                                  className={`AdminGroupAttendance__status-btn AdminGroupAttendance__status-btn--${status}`}
                                  title="Изменить"
                                >
                                  {getStatusText(status)}
                                </button>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="AdminGroupAttendance__legend">
              <div className="AdminGroupAttendance__legend-item">
                <span className="AdminGroupAttendance__legend-present">✓</span> - Присутствовал
              </div>
              <div className="AdminGroupAttendance__legend-item">
                <span className="AdminGroupAttendance__legend-absent">H</span> - Отсутствовал
              </div>
              <div className="AdminGroupAttendance__legend-item">
                <span className="AdminGroupAttendance__legend-unknown">-</span> - Не отмечено
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdminBottomNavBar />
    </div>
  );
};

export default AdminGroupAttendance;
